import { styled } from '@linaria/react';
import { useState } from 'react';
import { Icon } from '../../../../ui-library/icon/icon';
import { FloraBanner } from './flora-banner';
import type { FloraBannerVariant } from './flora-banner';

/**
 * A single notification rendered inside the card stack. Each item carries its
 * own content and handlers so the stack can page between unrelated nudges.
 */
interface FloraNotificationItem {
  /** Stable unique id — used as the React key and for callers' dismissal memory. */
  id: string;
  /** Title line — the primary message. */
  title: string;
  /** Optional supporting copy shown beneath the title. */
  description?: string;
  /** Optional Material icon name rendered as the leading accent. */
  icon?: string;
  /** Label for the call-to-action button; rendered only with `onAction`. */
  actionLabel?: string;
  /** Invoked when the call-to-action button is clicked. */
  onAction?: () => void;
  /** Invoked when this notification's dismiss (✕) is clicked. */
  onDismiss?: () => void;
  /** Visual tone; defaults to `'info'`. */
  variant?: FloraBannerVariant;
}

interface FloraNotificationStackProps {
  /** Active notifications to show, already filtered to those that should display. */
  notifications: FloraNotificationItem[];
}

/**
 * Renders a stack of Flora notifications as a single card, showing one at a
 * time. When more than one is active, a pager footer with ‹ › arrows lets the
 * user step through them; the count reflects the live list as items dismiss.
 *
 * The active index is clamped to the current length so dismissing the last
 * item (which shrinks the list) never leaves the pager pointing past the end.
 *
 * @param {FloraNotificationStackProps} props - The active notifications
 * @returns {JSX.Element | null} The card, or null when there are none
 */
export function FloraNotificationStack({ notifications }: FloraNotificationStackProps) {
  const [index, setIndex] = useState(0);
  const total = notifications.length;

  if (total === 0) return null;

  const current = Math.min(index, total - 1);
  const active = notifications[current];

  const goPrev = () => setIndex((prev) => (((Math.min(prev, total - 1) - 1) % total) + total) % total);
  const goNext = () => setIndex((prev) => (Math.min(prev, total - 1) + 1) % total);

  const footer =
    total > 1 ? (
      <Pager>
        <PagerCount>
          {current + 1} / {total}
        </PagerCount>
        <PagerNav>
          <PagerButton type="button" onClick={goPrev} aria-label="Previous notification">
            <Icon name="chevron_left" size={18} color="var(--ink-light)" />
          </PagerButton>
          <PagerButton type="button" onClick={goNext} aria-label="Next notification">
            <Icon name="chevron_right" size={18} color="var(--ink-light)" />
          </PagerButton>
        </PagerNav>
      </Pager>
    ) : undefined;

  return (
    <FloraBanner
      layout="card"
      icon={active.icon}
      title={active.title}
      description={active.description}
      actionLabel={active.actionLabel}
      onAction={active.onAction}
      onDismiss={active.onDismiss}
      variant={active.variant}
      footer={footer}
    />
  );
}

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const PagerCount = styled.span`
  font-size: 12px;
  color: var(--ink-lighter);
  font-family: var(--font-family);
  user-select: none;
`;

const PagerNav = styled.div`
  display: flex;
  gap: 4px;
`;

const PagerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  transition: background 0.12s;

  &:hover {
    background: var(--flora-hover-subtle);
  }
`;

export type { FloraNotificationItem, FloraNotificationStackProps };
